import { createAsyncThunk } from '@reduxjs/toolkit';

import ERRORS from '~/constants/errors';
import { qalTrackTechnicalErrorAction } from '~/thunks/analytics';

import { SUCCESS } from '../../constants/domain-specific';
import { selectPaymentData } from '../../selectors/selectPayment';
import { selectPolicyData } from '../../selectors/selectPolicyData';
import { getPolicyHeader } from '../../selectors/selectPolicyData/getPolicyHeader';
import { errorSchema, policyApi } from '../../services/policy';
import { quotesApi } from '../../services/quotes';
import { AppDispatch, RootState } from '../../store';
import { PaymentErrorCode, PaymentMethodOptions } from '../../types/payments';
import { PolicyBuilderMode } from '../../types/policy';
import { logError } from '../../utils/logError';
import { createAppAsyncThunk } from '../../utils/redux-helpers';
import { hasProperty } from '../../utils/types';
import { decline, genericDecline } from '../decline';
import { updatePolicyNumber } from '../quote';
import { addPaymentError } from './index';
import { paymentsSlice } from './slice';

const handleError = (
  e: unknown,
  {
    dispatch,
  }: {
    dispatch: AppDispatch;
  }
) => {
  if (hasProperty(e, 'data')) {
    const error = errorSchema.safeParse(e.data);
    if (error.success && error.data.errors?.[0]?.code.startsWith('PAYMENT_ERROR')) {
      dispatch(
        addPaymentError({
          code: error.data.errors[0].code as PaymentErrorCode,
          message: error.data.errors[0].message,
        })
      );
      dispatch(paymentsSlice.actions.updatePaymentState('error'));
    } else {
      logError(e);
      dispatch(genericDecline());
    }
  } else {
    logError(e);
  }
};

export const payMyBillAction = createAsyncThunk<
  void,
  { issueDocument: boolean },
  {
    state: RootState;
    dispatch: AppDispatch;
  }
>('payments/payMyBill', async (arg, { dispatch, getState, rejectWithValue }) => {
  try {
    let state = getState();
    dispatch(paymentsSlice.actions.updatePaymentState('in-progress'));
    const paymentData = selectPaymentData(state);
    state = getState();
    const { policy } = state.payMyBill;
    const dueDate = policy?.dueDate;
    if (!dueDate) return rejectWithValue('Cover start date expected');
    const coverStartFormatted = dueDate.split('+')[0];
    if (
      paymentData.paymentMethod === PaymentMethodOptions.CreditCard &&
      'transientToken' in paymentData &&
      paymentData.transientToken
    ) {
      const result = await dispatch(
        policyApi.endpoints.renew.initiate({
          policyNumber: policy.policyNumber,
          policy: {
            policyHeader: {
              ...getPolicyHeader({ coverStartFormatted }),
              issueDocument: arg.issueDocument,
            },
            payment: selectPaymentData(state),
          },
        })
      ).unwrap();
      dispatch(paymentsSlice.actions.updatePaymentState('success'));
      dispatch(
        paymentsSlice.actions.updatePaymentReference(
          result.messageMetadata.transactionInformation.identifier
        )
      );
    } else {
      dispatch(genericDecline('Unable to process payment'));
      logError(new Error('No transient token'));
    }
  } catch (e: unknown) {
    handleError(e, { dispatch });
  }
  return undefined;
});

export const bindPolicyAction = createAppAsyncThunk<unknown, void>(
  'payment/bindPolicy',
  async (_, { getState, dispatch, rejectWithValue, fulfillWithValue }) => {
    const state = getState();
    const policyData = selectPolicyData(state, {
      issueDocument: true,
      makePayment: true,
      mode: PolicyBuilderMode.Bind,
    });
    const quoteNumber =
      policyData?.policy?.policyHeader?.policyTransactionInformation?.policyNumber;

    if (!quoteNumber) {
      dispatch(decline(ERRORS.policyNumberNotFound));
    } else {
      try {
        const bindData = await dispatch(
          quotesApi.endpoints?.bind.initiate({
            quoteNumber,
            policyData: selectPolicyData(state, {
              issueDocument: true,
              makePayment: true,
              mode: PolicyBuilderMode.Bind,
            }),
          })
        ).unwrap();
        const { status } = bindData.messageMetadata.messageHeader;
        const policyNumber =
          bindData?.policy?.policyHeader?.policyTransactionInformation?.policyNumber;
        if (status !== SUCCESS || !policyNumber) {
          dispatch(genericDecline());
          dispatch(
            qalTrackTechnicalErrorAction({
              errorCode: '',
              message: 'Policy blinding failed',
              errorType: 'Policy blinding Error',
            })
          );
        } else {
          dispatch(updatePolicyNumber(policyNumber));
          dispatch(paymentsSlice.actions.updatePaymentState('success'));
        }
      } catch (e) {
        return rejectWithValue(e);
      }
    }
    return fulfillWithValue(undefined);
  }
);

export const payPolicyAction = createAppAsyncThunk<boolean>(
  'payments/payPolicy',
  async (arg, { dispatch, rejectWithValue, getState }) => {
    const state = getState();
    dispatch(paymentsSlice.actions.updatePaymentState('in-progress'));
    const paymentData = selectPaymentData(state);
    if (
      paymentData.paymentMethod === PaymentMethodOptions.CreditCard &&
      (!('transientToken' in paymentData) || !paymentData.transientToken)
    ) {
      dispatch(genericDecline(ERRORS.transientTokenNotFound));
      return false;
    }
    try {
      await dispatch(bindPolicyAction()).unwrap();
    } catch (e: unknown) {
      dispatch(paymentsSlice.actions.updatePaymentState('error'));
      return rejectWithValue(e);
    }
    return true;
  }
);
